import clsx from 'clsx';
import {
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  forwardRef,
  useId,
} from 'react';

/**
 * Primitivas visuais do portal do cliente (DESIGN-FOUNDATION §6.8).
 * Alvo de toque ≥ 48px; CTA e campo com 56px (§3.2). Nenhum hex literal —
 * tudo por token. Sem estado `disabled` cinza no CTA (§6.3): quando a ação
 * não é possível, o botão muda de rótulo e destino.
 */

type VarianteBotao = 'primario' | 'secundario' | 'terciario';

interface BotaoProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: VarianteBotao;
  carregando?: boolean;
}

export const Botao = forwardRef<HTMLButtonElement, BotaoProps>(function Botao(
  { variante = 'primario', carregando = false, className, children, disabled, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      aria-busy={carregando || undefined}
      disabled={disabled ?? carregando}
      className={clsx(
        'tipo-botao inline-flex min-h-[48px] items-center justify-center gap-2.5 rounded-1 px-4 transition-colors',
        variante === 'primario' &&
          'min-h-[56px] w-full bg-acento text-white active:bg-acento-pressed dark:border dark:border-[color:var(--foco)]',
        variante === 'secundario' &&
          'min-h-[56px] w-full border border-linha-forte bg-superficie-alta text-tinta-forte active:bg-superficie',
        variante === 'terciario' && 'text-tinta-2 active:text-tinta-forte',
        (disabled ?? carregando) && 'opacity-70',
        className,
      )}
      {...rest}
    >
      {carregando && (
        <span
          aria-hidden
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  );
});

interface CampoProps extends InputHTMLAttributes<HTMLInputElement> {
  rotulo: string;
  ajuda?: string;
  erro?: string;
}

export const Campo = forwardRef<HTMLInputElement, CampoProps>(function Campo(
  { rotulo, ajuda, erro, id, className, ...rest },
  ref,
) {
  const gerado = useId();
  const campoId = id ?? gerado;
  const ajudaId = `${campoId}-ajuda`;
  const erroId = `${campoId}-erro`;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={campoId} className="tipo-corpo text-tinta-2">
        {rotulo}
      </label>
      <input
        ref={ref}
        id={campoId}
        className={clsx(
          'tipo-corpo h-[56px] rounded-1 border bg-superficie-alta px-4 text-tinta-forte',
          'focus:border-tinta-forte',
          erro ? 'border-erro' : 'border-linha-forte',
          className,
        )}
        aria-invalid={erro ? true : undefined}
        aria-describedby={clsx(ajuda && !erro && ajudaId, erro && erroId) || undefined}
        {...rest}
      />
      {ajuda && !erro && (
        <span id={ajudaId} className="tipo-nota text-tinta-2">
          {ajuda}
        </span>
      )}
      {/* erro: só borda + mensagem, sem anel — distingue foco de erro para quem tem
          dificuldade com cor (DESIGN-FOUNDATION §6.8) */}
      {erro && (
        <span id={erroId} className="tipo-nota text-erro">
          {erro}
        </span>
      )}
    </div>
  );
});

/** Checkbox dos termos — caixa 24px, alvo 48px, nunca pré-marcada (§6.8 / LGPD). */
export function CheckboxTermos({
  marcado,
  aoMudar,
  children,
  erro,
}: {
  marcado: boolean;
  aoMudar: (v: boolean) => void;
  children: ReactNode;
  erro?: string;
}): ReactNode {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="flex min-h-[48px] items-center gap-3">
        <input
          id={id}
          type="checkbox"
          checked={marcado}
          onChange={(e) => aoMudar(e.target.checked)}
          style={{ accentColor: 'var(--acento)' }}
          className="h-6 w-6 shrink-0 rounded-1 border border-linha-forte"
          aria-invalid={erro ? true : undefined}
        />
        <span className="tipo-corpo text-tinta">{children}</span>
      </label>
      {erro && <span className="tipo-nota text-erro">{erro}</span>}
    </div>
  );
}

/** Bloco de aviso — nível 2, `--aviso-fraco` (§6.8). Não é vermelho: é informação. */
export function BlocoAviso({
  children,
  aoTentarDeNovo,
}: {
  children: ReactNode;
  aoTentarDeNovo?: () => void;
}): ReactNode {
  return (
    <div className="rounded-2 border-l-2 border-aviso bg-aviso-fraco px-4 py-3">
      <p className="tipo-corpo text-tinta">{children}</p>
      {aoTentarDeNovo && (
        <button
          type="button"
          onClick={aoTentarDeNovo}
          className="tipo-botao mt-2 text-acento-texto underline"
        >
          Tentar de novo
        </button>
      )}
    </div>
  );
}

/** Falha de rede — nunca tela em branco, nunca spinner sozinho (§6.8). */
export function FalhaRede({ aoTentarDeNovo }: { aoTentarDeNovo: () => void }): ReactNode {
  return <BlocoAviso aoTentarDeNovo={aoTentarDeNovo}>Não conseguimos carregar agora.</BlocoAviso>;
}

/** Esqueleto — bloco `--poco` estático, sem shimmer (§4.1). */
export function Esqueleto({ className }: { className?: string }): ReactNode {
  return <div className={clsx('bg-poco', className)} aria-hidden />;
}
